import { useCallback, useState } from "react";
import { useErrorToast } from "@/components/toast/useErrorToast";
import { useActivateService } from "@/hooks/useApi";

// "Set as default" on an LLM service: switches the Agent's / AI Helper's
// model. useActivateService invalidates the services query, so the Status
// column and the detail page's badge move to the new default on their own;
// a failure shows as an error toast. Shared by the Services list's row menu
// and the service detail's header button. One switch at a time: a call while
// another is in flight is dropped, so two activations can't race and leave
// whichever finished last as the default.
export function useSetDefaultLlm() {
  const { mutateAsync: activate, isPending } = useActivateService();
  const [error, setError] = useState<string | null>(null);
  const clearError = useCallback(() => setError(null), []);
  useErrorToast(error, clearError);

  const setDefault = useCallback(
    async (name: string) => {
      if (isPending) return;
      try {
        await activate(name);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to set the default model",
        );
      }
    },
    [activate, isPending],
  );

  return { setDefault, isPending };
}
